// NumberInput — controlled plain-number text input (quantities, counts with
// fractional parts) sharing CurrencyInput's display contract: while focused it
// shows the caller's raw stored value verbatim so typing and backspace behave
// naturally; once blurred, a parseable value is redisplayed with thousands
// grouping (decimals preserved as parsed, no forced 2dp and no $ adornment).
// Comma-grouped input — typed or pasted from SAP/spreadsheets — parses via the
// shared comma-tolerant parser. onChange always reports the raw text upward,
// never the formatted display string.
import { useState, type ChangeEvent } from "react";
import { clsx } from "clsx";
import { parseNumericText } from "../../utils/numericText";

export interface NumberInputProps {
  id?: string;
  value: string;
  onChange: (raw: string) => void;
  ariaLabel?: string;
  ariaInvalid?: boolean;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  size?: "sm" | "md";
}

const GROUPED_FORMATTER = new Intl.NumberFormat("en-AU", {
  maximumFractionDigits: 10,
});

// Blurred display value: a parseable number is regrouped; anything blank,
// partial, or unparseable falls back to the raw text so an in-progress edit is
// never silently mangled.
function displayValue(value: string): string {
  if (value.trim() === "") return "";
  const n = parseNumericText(value);
  if (n === null) return value;
  return GROUPED_FORMATTER.format(n);
}

export default function NumberInput({
  id,
  value,
  onChange,
  ariaLabel,
  ariaInvalid,
  className,
  disabled,
  placeholder,
  size = "md",
}: NumberInputProps) {
  const [focused, setFocused] = useState(false);

  const shown = focused ? value : displayValue(value);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  return (
    <input
      id={id}
      type="text"
      inputMode="decimal"
      aria-label={ariaLabel}
      aria-invalid={ariaInvalid}
      className={clsx(
        size === "sm" ? "input-sm" : "input",
        "tabular-nums",
        className,
      )}
      value={shown}
      onChange={handleChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      disabled={disabled}
      placeholder={placeholder}
    />
  );
}
